import { addDays, parse } from 'date-fns/esm';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from '../../../../../../components/Button';
import { Calendar } from '../../../../../../components/Calendar';
import { Caption } from '../../../../../../components/Caption';
import { Card } from '../../../../../../components/Card';
import { ErrorDialogContext } from '../../../../../../components/ErrorDialog';
import { Modal } from '../../../../../../components/Modal';
import { Routes } from '../../../../../../constants/Routes';
import { Dimension } from '../../../../../../constants/Style';
import { useContext } from '../../../../../../hooks/useContext';
import { useMedia } from '../../../../../../hooks/useMedia';
import { useReservationId } from '../../../../../../hooks/useReservationId';
import { useTransaction } from '../../../../../../hooks/useTransaction';
import { SessionStore } from '../../../../../../stores/SessionStore';
import { SignInMethod } from '../../../../../Accounts/AccountsNew';
import { ConditionalWrapper } from '../../../../../Reservations/ReservationNew/VacancySelect';
import { CheckupVacancyStore } from '../../../../Reservations/CheckupReservationsNew/CheckupReservationVacancySelect/stores/CheckupVacancyStore';
import { IndividualCheckupReservationStore } from '../stores/IndividualCheckupReservationStore';
import { IndividualCheckupReservationConfirmDialog } from './component/IndividualCheckupReservationConfirmDialog';

type Props = {
  onNext?: () => void;
  onBack?: () => void;
};

export const IndividualCheckupVacancySelectPage = observer(({ onBack }: Props) => {
  const wrapped = useMedia([{ query: `(min-width: ${Dimension.ContentWidthMax}px)`, value: true }], false);
  const store = useContext(IndividualCheckupReservationStore.Context);
  const vacancyStore = useContext(CheckupVacancyStore.Context);
  const session = useContext(SessionStore.Context);
  const { setError } = useContext(ErrorDialogContext);

  const [week, setWeek] = useState(new Date());
  const { push, encode } = useReservationId();
  const history = useHistory();
  const [load, loadStatus] = useTransaction(
    async () =>
      store.department &&
      vacancyStore.fetchVacancies(
        week,
        store.department,
        store.options.map(o => o.id),
        store.treatmentKind,
      ),
  );
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    load();
    store.department &&
      vacancyStore.prefetchNext(
        week,
        store.department,
        store.options.map(o => o.id),
        store.treatmentKind,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week]);

  const onSelect = ({ date }: { date: Date }) => {
    const selected = vacancyStore.getSelected(date);
    if (!selected) {
      return;
    }
    store.setDetails([
      {
        date,
        department: store.department,
        duration: selected.checkup.estimatedDuration,
        laneId: selected.checkup.laneId,
      } as any,
      ...(selected.dental
        ? [
            {
              date: parse(`${selected.dental.date} ${selected.dental.time}`, 'yyyy-MM-dd HH:mm', new Date()),
              duration: selected.dental.estimatedDuration || 0,
              laneId: selected.dental.laneId || '',
            } as any,
          ]
        : []),
    ]);
    setConfirm(true);
  };

  const [commit, commitStatus] = useTransaction(
    async (signInMethod?: SignInMethod) => {
      setConfirm(false);
      if (!session.authenticated) {
        const id = await store.postTemporallyTreatment();
        history.push({
          pathname: Routes.Paths.AccountNew,
          search: new URLSearchParams({
            type: signInMethod,
            reservationId: encode(id || ''),
            action: 'fixCheckupTreatment',
          } as Record<'type' | 'reservationId' | 'action', string>).toString(),
        });
        return;
      }
      const id = await store.postTreatment();
      push(Routes.Paths.CheckupReservationCompleteIndividual, id);
    },
    e => {
      const gqlError: { errors?: { message: string }[] } = e as any;
      setError({
        header: '予約に失敗しました',
        message: `${
          gqlError.errors ? gqlError.errors[0].message : ''
        }お手数ですが、再度はじめから予約をお願い致します。`,
        onOk: () => {
          setError();
          window.location.reload();
        },
      });
    },
  );

  return (
    <>
      <ConditionalWrapper
        conditional={
          <>
            {vacancyStore.vacancies ? (
              <Calendar
                onSelect={onSelect}
                items={vacancyStore.vacancies}
                now={new Date()}
                onClickNextWeek={() => setWeek(addDays(week, 7))}
                onClickPrevWeek={() => setWeek(addDays(week, -7))}
                alertThreshold={0}
              />
            ) : (
              <></>
            )}
          </>
        }
        wrapped={wrapped}
      >
        <Modal visible={!!loadStatus.running || !!commitStatus.running}>
          <Card>予約状況を確認中...</Card>
        </Modal>
        <Caption type="header" underline align="center">
          クリニックのご予約
        </Caption>
        <Button ghost block onClick={onBack}>
          オプション選択に戻る
        </Button>
        <IndividualCheckupReservationConfirmDialog
          handleStateChange={setConfirm}
          open={confirm}
          onConfirm={commit}
          disabled={!!commitStatus.running}
        />
      </ConditionalWrapper>
    </>
  );
});
